import { createApi } from '@reduxjs/toolkit/query/react';
import { createBackendBaseQuery, transformApiError } from '../../utils/createAuthBaseQuery';
import { API_URLS } from '../../constants/apiUrl';

export interface MotorInvestigation {
  id: string;
  mxId: string;
  region: string;
  stdOrVsd: string;
  modelName: string;
  oldMotorPn: number;
  newMotorPn: number;
  ticketNumber: string;
  powerKw: string;
  voltageV: string;
  frequencyHz: string;
  status: string;
}

export interface MotorInvestigationSearchResponse {
  totalResult: number;
  activeResult: number;
  motorInvestigations: MotorInvestigation[];
}

export interface FetchMotorInvestigationParams {
  page?: number;
  limit?: number;
  sortBy?: keyof MotorInvestigation;
  order?: 'asc' | 'desc';
  [key: string]: string | number | undefined;
}

export interface MotorInvestigationPart {
  flag: boolean;
  legend: string;
  quantity: number;
  partNumber: string;
  partDescription: string;
  motorInvestigationPartMasterId: number;
}

export interface MotorInvestigationDetails {
  id: string;
  oldMotorNumber: number;
  newMotorNumber: number;
  machineTypeId: number;
  machineTypeName: string | null;
  voltage: number;
  frequency: number;
  power: number;
  regionId: number;
  oldMotorArrangementId: number;
  oldMotorArrangementBlobStorageReference: string;
  newMotorArrangementId: number;
  newMotorArrangementBlobStorageReference: string;
  oldMotorArrangementParts: MotorInvestigationPart[];
  newMotorArrangementParts: MotorInvestigationPart[];
  fieldInstruction: string;
  internalNote: string;
  attachedFiles: string[];
}

export interface SaveMotorSolutionLogPayload {
  id?: number;
  motorNumber?: number;
  serialNumber?: string;
  region?: string;
  isAlternativeProvided?: boolean;
  isTicketCreationStarted?: boolean;
  isTicketCreated?: boolean;
  ticketFailedReason?: string;
}

const BASE_URL = process.env.REACT_APP_MOTER_SERVICE_API_BASE_URL as string;

if (!BASE_URL) {
  throw new Error('REACT_APP_MOTER_SERVICE_API_BASE_URL environment variable is not set');
}

// Create the base query using the reusable factory
const baseQuery = createBackendBaseQuery(BASE_URL);

export const motorInvestigationApiSlice = createApi({
  reducerPath: 'motorInvestigationApi',
  baseQuery: baseQuery,
  tagTypes: ['MotorInvestigation'],
  endpoints(builders) {
    return {
      fetchMotorInvestigations: builders.query<
        MotorInvestigationSearchResponse,
        FetchMotorInvestigationParams | void
      >({
        query: (params) => {
          let queryString = '';
          if (params) {
            const usp = new URLSearchParams();
            Object.entries(params).forEach(([key, value]) => {
              if (value !== undefined && value !== null && value !== '') {
                usp.append(key, String(value));
              }
            });
            queryString = usp.toString() ? `?${usp.toString()}` : '';
          }
          return {
            url: `${API_URLS.MOTOR_SERVICE.SEARCH}${queryString}`,
            method: 'GET',
          };
        },
        // The list is tagged once as 'LIST' so only list-changing mutations refetch it.
        providesTags: [{ type: 'MotorInvestigation', id: 'LIST' }],
        transformErrorResponse: transformApiError,
      }),
      // Fetch the full details of a single motor investigation by its GUID id.
      fetchMotorInvestigationDetails: builders.query<MotorInvestigationDetails, string>({
        query: (id) => ({
          url: `${API_URLS.MOTOR_INVESTIGATION.DETAILS}?id=${id}`,
          method: 'GET',
        }),
        // Tagged per-investigation so a save only invalidates that one record.
        providesTags: (_result, _error, id) => [{ type: 'MotorInvestigation', id }],
        transformErrorResponse: transformApiError,
      }),
      // Save the full details of a motor investigation.
      saveMotorInvestigationDetails: builders.mutation<
        MotorInvestigationDetails,
        MotorInvestigationDetails
      >({
        query: (details) => ({
          url: API_URLS.MOTOR_INVESTIGATION.SAVE_DETAILS,
          method: 'POST',
          body: details,
        }),
        // Invalidate this investigation's details and the list (edited fields show there).
        invalidatesTags: (_result, _error, arg) => [
          { type: 'MotorInvestigation', id: arg.id },
          { type: 'MotorInvestigation', id: 'LIST' },
        ],
        transformErrorResponse: transformApiError,
      }),
      // Archive (deactivate) a motor investigation
      archiveMotorInvestigation: builders.mutation<boolean, string>({
        query: (investigationId) => ({
          url: `${API_URLS.MOTOR_INVESTIGATION.ARCHIVE}?id=${investigationId}`,
          method: 'POST',
        }),
        // Membership/status change → refresh the list.
        invalidatesTags: [{ type: 'MotorInvestigation', id: 'LIST' }],
        transformErrorResponse: transformApiError,
      }),
      // UnArchive (activate) a motor investigation.
      // Returns -1 when activation fails validation (e.g. the related motor is inactive).
      unArchiveMotorInvestigation: builders.mutation<number, string>({
        query: (investigationId) => ({
          url: `${API_URLS.MOTOR_INVESTIGATION.UNARCHIVE}?id=${investigationId}`,
          method: 'POST',
        }),
        invalidatesTags: [{ type: 'MotorInvestigation', id: 'LIST' }],
        transformErrorResponse: transformApiError,
      }),
      // Delete a motor investigation.
      // returns true if the deletion was successful, false otherwise.
      deleteMotorInvestigation: builders.mutation<boolean, string>({
        query: (investigationId) => ({
          url: `${API_URLS.MOTOR_INVESTIGATION.DELETE_RECORD}?id=${investigationId}`,
          method: 'POST',
        }),
        invalidatesTags: [{ type: 'MotorInvestigation', id: 'LIST' }],
        transformErrorResponse: transformApiError,
      }),
      // Create/update a motor solution log entry. Returns the persisted log id.
      saveMotorSolutionLog: builders.mutation<number, SaveMotorSolutionLogPayload>({
        query: (payload) => ({
          url: API_URLS.MOTOR_SERVICE.SOLUTION_LOG,
          method: 'POST',
          body: payload,
        }),
        transformErrorResponse: transformApiError,
      }),
    };
  },
});

export const {
  useFetchMotorInvestigationsQuery,
  useLazyFetchMotorInvestigationsQuery,
  useFetchMotorInvestigationDetailsQuery,
  useLazyFetchMotorInvestigationDetailsQuery,
  useSaveMotorInvestigationDetailsMutation,
  useArchiveMotorInvestigationMutation,
  useUnArchiveMotorInvestigationMutation,
  useDeleteMotorInvestigationMutation,
  useSaveMotorSolutionLogMutation,
} = motorInvestigationApiSlice;
